<script setup lang="ts">
import type {
  FormField,
  FormFieldChoice,
  GraphData,
  GraphDataSet,
  GraphDatasetData
} from '@/models';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import Chart from 'primevue/chart';
import * as ChartJS from 'chart.js/auto';
import ProgressSpinner from 'primevue/progressspinner';
import DataGraphDialog from '../DataGraphDialog.vue';
import Checkbox from 'primevue/checkbox';
import zoomPlugin from 'chartjs-plugin-zoom';
import { LegendMarginPlugin, VerticalLinePlugin } from '@/utils/chartJsHelper';
import { ButtonType, CustomButton } from '@/components/common';
import { PUMP_GRAPH_COLORS } from '@/utils';

const props = defineProps({
  caseId: {
    type: String
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  timeSeriesTimepoints: {
    type: Object as PropType<FormFieldChoice[]>,
    required: true
  }
});

const localField = ref<FormField>(props.field);

const chartType = computed(() => {
  return props.field.plotType === 'Barplot' ? 'bar' : 'line';
});

const graphOptions = computed(() => {
  if (chartType.value === 'bar') {
    return {
      scales: {
        x: {
          type: 'category',
          stacked: true,
          title: {
            display: true,
            text: 'Perfusion Timepoint',
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#000'
          },
          ticks: {
            stepSize: 1,
            color: '#000',
            font: {
              size: 16
            }
          }
        },
        y: {
          beginAtZero: true,
          stacked: false,
          title: {
            display: true,
            text: props.field.yAxisLabel ?? props.field.label,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#000'
          },
          ticks: {
            color: '#000',
            font: {
              size: 16
            }
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            filter: (legendItem: unknown, data: unknown) => {
              // Only show legend for datasets with a non-empty label
              //@ts-expect-error just the unknown datatypes
              return (data.datasets[legendItem.datasetIndex].label !== '');
            }
          },
          generateLabels: (chart: ChartJS.Chart) => {
            return chart.data.datasets.map((dataset: ChartJS.ChartDataset, index) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              hidden: !chart.isDatasetVisible(index),
              pointStyle: dataset.label == 'Active liver' || dataset.label == 'Historical median' ? 'circle' : 'rect',
              pointStyleWidth: dataset.label === 'Active liver' || dataset.label == 'Historical median' ? undefined : 40,
              datasetIndex: index
            }));
          },
          usePointStyle: true
        }
      }
    };
  }

  return {
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        min: props.field.graphData?.xAxisMin !== undefined ? props.field.graphData.xAxisMin : undefined,
        max: props.field.graphData?.xAxisMax !== undefined ? props.field.graphData.xAxisMax : undefined,
        ticks: {
          stepSize: props.field.graphData?.xAxisStep !== undefined ? props.field.graphData.xAxisStep : 1,
          color: '#000',
          font: {
            size: 16
          }
        },
        title: {
          display: true,
          text: 'Perfusion time (hours)',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#000'
        }
      },
      y: {
        title: {
          display: true,
          text: props.field.yAxisLabel ?? props.field.label,
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#000'
        },
        min: props.field.graphData?.yAxisMin !== undefined ? props.field.graphData.yAxisMin : undefined,
        max: props.field.graphData?.yAxisMax !== undefined ? props.field.graphData.yAxisMax : undefined,
        ticks: {
          stepSize: props.field.graphData?.yAxisStep !== undefined ? props.field.graphData.yAxisStep : undefined,
          color: '#000',
          font: {
            size: 16
          }
        }
      }
    },
    layout: {
      padding: 0
    },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy'
        },
        zoom: {
          pinch: {
            enabled: true // Enables pinch-to-zoom
          },
          wheel: {
            enabled: true // Enables wheel-based zoom
          },
          mode: 'xy'
        }
      },
      verticalLines: [
        {
          x: 0,
          color: 'black',
          lineWidth: 1,
          dash: [6, 6]
        }
      ],
      tooltip: {
        events: ['click', 'mousemove', 'mouseout'],
        callbacks: {
          // Customizes the title (top line of tooltip)
          title: (tooltipItems: unknown[]) => {
            const item = tooltipItems[0];
            //@ts-expect-error just the unknown datatypes
            return `Timepoint: ${Number(item.parsed.x).toFixed(2)}`;
          },
          // Customizes the label (each line in tooltip)
          label: (tooltipItem: unknown) => {
            //@ts-expect-error just the unknown datatypes
            if (tooltipItem.dataset.label === 'Sodium Bicarbonate (8.4%)') {
              //@ts-expect-error just the unknown datatypes
              return `Volume: ${tooltipItem.raw.volume}`;
            }
            //@ts-expect-error just the unknown datatypes
            if (tooltipItem.dataset.label === 'Dextrose/Glucose/TPN bolus') {
              //@ts-expect-error just the unknown datatypes
              return `Volume: ${tooltipItem.raw.volume}`;
            }
            //@ts-expect-error just the unknown datatypes
            if (tooltipItem.dataset.tooltipLabel) {
              //@ts-expect-error just the unknown datatypes
              return `${tooltipItem.dataset.tooltipLabel}: ${Number(tooltipItem.parsed.y).toFixed(2)}`;
            }
            //@ts-expect-error just the unknown datatypes
            return `${tooltipItem.dataset.label}: ${Number(tooltipItem.parsed.y).toFixed(2)}`;
          }
        }
      },
      legend: {
        labels: {
          color: '#333',
          font: {
            size: 16
          },
          filter: (legendItem: unknown, data: unknown) => {
            // Only show legend for datasets with a non-empty label
            return (
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== '' &&
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== 'CIupper' &&
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== 'CIlower2' && data.datasets[legendItem.datasetIndex].label !== 'CIupper2'
            );
          },
          generateLabels: (chart: ChartJS.Chart) => {
            return chart.data.datasets.map((dataset: ChartJS.ChartDataset, index) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              hidden: !chart.isDatasetVisible(index),
              pointStyle: dataset.label == 'Sodium Bicarbonate (8.4%)' ? 'circle' : 'rect',
              pointStyleWidth: dataset.label === 'Sodium Bicarbonate (8.4%)' ? undefined : 40,
              datasetIndex: index
            }));
          },
          usePointStyle: true
        }
      }
    },
    animation: false,
    spanGaps: true,
    responsive: true,
    maintainAspectRatio: false,
    datasets: {
      line: {
        pointRadius: 4,
        pointHoverRadius: 4,
        pointHoverBorderWidth: 4,
        pointBorderColor: '#0265de',
        pointHoverBorderColor: '#0265de'
      }
    },
    elements: {
      point: {
        radius: 0
      },
      line: {
        tension: 0.4,
        borderWidth: 3,
        hoverBorderWidth: 5,
        backgroundColor: '#0265de',
        borderColor: '#0265de',
        borderCapStyle: 'round',
        borderJoinStyle: 'round'
      }
    }
  };
});

watch(() => props.field.loading, () => {
  if ((props.field.variableName === 'NMP_PORTAL_VEIN_FLOW' || props.field.variableName === 'TOTAL_BILE_PRODUCTION') && !props.field.loading) {
    prepareGraphData();
  }
});

onMounted(() => {
  localField.value = props.field;
  ChartJS.Chart.register(VerticalLinePlugin, zoomPlugin, LegendMarginPlugin);
  prepareGraphData();
});

const chartRef = ref();

const reset = () => {
  if (chartRef.value) {
    const chart = chartRef.value.getChart();
    chart.resetZoom();

    chart.data.datasets.forEach((_dataset: unknown, index: number) => {
      chart.show(index);
    });
  }
};

const graphData = ref<GraphData>();
const showBaseLineData = ref<boolean>(false);
const showLiverKgData = ref<boolean>(props.field.perLiverKgOptionChecked || false);

const emit = defineEmits(['liverKgDataChange']);

watch(() => showBaseLineData.value, () => {
  reset();
  prepareGraphData();
});

watch(() => showLiverKgData.value, () => {
  reset();
  emit('liverKgDataChange', showLiverKgData.value, localField.value);
});

// Mirrors Chart.js's auto nice-tick algorithm: given all y-values across all
// datasets, compute the step size that Chart.js would use (~5 ticks).
const computeYAxisStepSize = (...datasets: GraphDatasetData[][]): number => {
  const allY = datasets.flat().map(p => Number(p.y)).filter(Number.isFinite);
  if (allY.length < 2) return 1;

  const yMin = props.field.graphData?.yAxisMin ?? Math.min(...allY);
  const yMax = props.field.graphData?.yAxisMax ?? Math.max(...allY);
  const range = yMax - yMin;
  if (range === 0) return 1;

  // Aim for ~5 ticks, then round to the nearest 1 / 2 / 5 / 10 multiple
  const roughStep = range / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const fraction = roughStep / magnitude;

  let niceFraction: number;
  if (fraction < 1.5) niceFraction = 1;
  else if (fraction < 3) niceFraction = 2;
  else if (fraction < 7) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * magnitude;
};

const prepareGraphData = () => {
  if (chartType.value === 'bar') {
    const labels: string[] = [];
    const rangeData: ([number, number] | null)[] = [];
    const range2Data: ([number, number] | null)[] = [];
    const historicalValueData: ([number, number] | null)[] = [];
    const valueData: ([number, number] | null)[] = [];

    if (props.field.variableName === 'TOTAL_BILE_PRODUCTION') {
      let totalBile : { value : number | undefined } = { value: undefined};
      if (props.field.value) {
        try {
          totalBile = JSON.parse(props.field.value);
        } catch (e) {
          // If parsing fails, fallback to 0
          totalBile = { value: 0 };
        }
      } else {
        totalBile = { value: 0 };
      }
      if (totalBile.value) {
        valueData.push([Number(totalBile.value), totalBile.value]);
      }
    } else {
      props.field.timeSeriesData?.forEach(data => {
        if (data.value) {
          valueData.push([Number(data.value), data.value]);
        }
      });
    }

    const allTimepoints = props.field.historicalData?.map(
      item => item.perfusionActualTimepoint || `${item.perfusionSelectedTimepoint}`
    ) || [];

    allTimepoints.forEach(timepoint => {
      labels.push(`${timepoint}`);
      const item = props.field.historicalData?.find(
        d => (d.perfusionActualTimepoint || `${d.perfusionSelectedTimepoint}`) === timepoint
      );

      rangeData.push(item && item.ymin && item.ymax ? [Number(item.ymin), Number(item.ymax)] : null);
      range2Data.push(
        item &&
        item.ymin2 !== null && item.ymin2 !== undefined &&
        item.ymax2 !== null && item.ymax2 !== undefined ?
          [Number(item.ymin2), Number(item.ymax2)] :
          null
      );
      
      if (item) {
        historicalValueData.push([Number(item.value), Number(item.value)]);
      } else {
        historicalValueData.push(null);
      }
    });

    const datasets: GraphDataSet[] = [];

    datasets.push({
      label: 'Active liver',
      type: 'scatter',
      data: valueData,
      backgroundColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER],
      pointRadius: 8, // Size of the points
      pointHoverRadius: 10
    });

    if (!showLiverKgData.value) {
      // Dataset for the value "point", drawn as a thin bar/line
      datasets.push({
        label: `Median (${localField.value.liverCount} PUMP livers)`,
        type: 'scatter',
        data: historicalValueData,
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
        pointRadius: 8, // Size of the points
        pointHoverRadius: 10
      });

      // Dataset for the faint, outer range
      datasets.push({
        label: 'Central 95% of values',
        data: range2Data,
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
        barPercentage: 0.2
      });

      // Dataset for the dark, inner range
      datasets.push({
        label: '95% CI of the median',
        data: rangeData,
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
        barPercentage: 0.2
      });
    }

    const centerRangeData: ([number, number] | null)[] = [];
    const centerRange2Data: ([number, number] | null)[] = [];
    const centerHistoricalValueData: ([number, number] | null)[] = [];

    const allCenterTimepoints = props.field.centerHistoricData?.map(
      item => item.perfusionActualTimepoint || `${item.perfusionSelectedTimepoint}`
    ) || [];

    allCenterTimepoints.forEach(timepoint => {
      const item = props.field.centerHistoricData?.find(
        d => (d.perfusionActualTimepoint || `${d.perfusionSelectedTimepoint}`) === timepoint
      );

      centerRangeData.push(item && item.ymin && item.ymax ? [Number(item.ymin), Number(item.ymax)] : null);
      centerRange2Data.push(
        item &&
        item.ymin2 !== null && item.ymin2 !== undefined &&
        item.ymax2 !== null && item.ymax2 !== undefined ?
          [Number(item.ymin2), Number(item.ymax2)] :
          null
      );
      
      if (item) {
        centerHistoricalValueData.push([Number(item.value), Number(item.value)]);
      } else {
        centerHistoricalValueData.push(null);
      }
    });

    if (centerRangeData.length > 0 && centerRange2Data.length > 0) {
      datasets.push({
        label: `Median (${localField.value.centerLiverCount} Center livers)`,
        type: 'scatter',
        data: centerHistoricalValueData,
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
        pointRadius: 8, // Size of the points
        pointHoverRadius: 10
      });

      // Dataset for the faint, outer range
      datasets.push({
        label: 'Central 95% of values',
        data: centerRange2Data,
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
        barPercentage: 0.2
      });

      // Dataset for the dark, inner range
      datasets.push({
        label: '95% CI of the median',
        data: centerRangeData,
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
        barPercentage: 0.2
      });
    }

    graphData.value = {
      labels: labels,
      datasets: datasets
    };
    return;
  }

  const values: (GraphDatasetData | null)[] = [];
  if (localField.value.cumulativeTimeSeriesData) {
    localField.value.cumulativeTimeSeriesData.forEach(data => {
      values.push({
        x: data.perfusionActualTimepoint!,
        y: data.value
      });
    });
  } else {
    localField.value.timeSeriesData?.forEach(data => {
      if (data.value) {
        values.push({
          x: data.perfusionActualTimepoint!,
          y: data.value
        });
      }
    });
  }

  const dataset: GraphDataSet = {
    label: 'Active liver',
    data: values,
    backgroundColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER],
    borderColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER]
  };

  const pumpValues: GraphDatasetData[] = [];
  const pumpMinValues: GraphDatasetData[] = [];
  const pumpMaxValues: GraphDatasetData[] = [];
  const pumpMinValues2: GraphDatasetData[] = [];
  const pumpMaxValues2: GraphDatasetData[] = [];

  for (const data of props.field.historicalData || []) {
    if (!showBaseLineData.value && data.perfusionActualTimepoint < 0) {
      continue;
    }

    pumpValues.push({
      x: data.perfusionActualTimepoint!,
      y: data.value
    });
    pumpMinValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymin
    });
    pumpMaxValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymax
    });
    if (data.ymin2 !== null && data.ymin2 !== undefined && data.ymax2 !== null && data.ymax2 !== undefined) {
      pumpMinValues2.push({
        x: data.perfusionActualTimepoint,
        y: data.ymin2
      });
      pumpMaxValues2.push({
        x: data.perfusionActualTimepoint,
        y: data.ymax2
      });
    }
  }

  const pumpLiverDataset: GraphDataSet = {
    label: `Mean trajectory (${localField.value.liverCount} PUMP livers)`,
    tooltipLabel: 'Mean',
    data: pumpValues,
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
    borderColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderColor: '#bf02ef',
    pointHoverBorderColor: '#bf02ef'
  };

  const minDataset: GraphDataSet = {
    label: '95% CI of the mean',
    tooltipLabel: '95% CI - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
    fill: '+1'
  };

  const maxDataset: GraphDataSet = {
    label: 'CIupper',
    tooltipLabel: '95% CI - high',
    data: pumpMaxValues,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
    fill: false
  };

  const minDataset2: GraphDataSet = {
    label: 'Central 95% of trajectories',
    tooltipLabel: '95% data - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues2,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
    fill: '+1'
  };

  const maxDataset2: GraphDataSet = {
    label: 'CIupper2',
    tooltipLabel: '95% data - high',
    data: pumpMaxValues2,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'], 
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
    fill: false
  };

  // Only use primary data (not CI bands) to compute step — CI extremes inflate the
  // range and cause the algorithm to return a step 2x larger than what Chart.js renders.
  const yStepSize = props.field.graphData?.yAxisStep ??
    computeYAxisStepSize(values as GraphDatasetData[], pumpValues);

  const yAxisMin = props.field.graphData?.yAxisMin ?? 0;
  const calculatedInfusionY = yAxisMin + yStepSize;

  const glucoseInfusionStart = localField.value.glucoseData
    ?.filter(data => data.medicationType === 'infusion')
    .map(data => Number(data.perfusionActualTimepoint))
    .filter(value => Number.isFinite(value));

  const tpnInfusionStart = localField.value.tpnData
    ?.filter(data => data.medicationType === 'infusion')
    .map(data => Number(data.perfusionActualTimepoint))
    .filter(value => Number.isFinite(value));

  const earliestGlucoseInfusion = glucoseInfusionStart && glucoseInfusionStart.length > 0 ?
    Math.min(...glucoseInfusionStart) :
    undefined;
  const earliestTpnInfusion = tpnInfusionStart && tpnInfusionStart.length > 0 ?
    Math.min(...tpnInfusionStart) :
    undefined;

  const glucoseStartsLater = earliestGlucoseInfusion !== undefined && earliestTpnInfusion !== undefined &&
    earliestGlucoseInfusion > earliestTpnInfusion;

  const glucoseInfusionY = glucoseStartsLater ? calculatedInfusionY : 0;
  const tpnInfusionY = glucoseStartsLater ? 0 : calculatedInfusionY;

  const markerPoints: GraphDatasetData[] = [];

  localField.value.bicarbonateData?.forEach(data => {
    markerPoints.push({
      x: data.perfusionActualTimepoint!,
      y: data.value,
      //@ts-expect-error: we are using a custom property
      volume: data.volume
    });
  });

  const markerDataset: GraphDataSet = {
    label: 'Sodium Bicarbonate (8.4%)',
    type: 'scatter',
    data: markerPoints,
    backgroundColor: ['#ff0000'],
    borderColor: ['#ff0000'],
    pointRadius: 8
  };

  const glucoseMarkerPoints: GraphDatasetData[] = [];
  const glucoseMarkerValues: GraphDatasetData[] = [];

  localField.value.glucoseData?.forEach(data => {
    if (data.medicationType === 'bolus') {
      glucoseMarkerPoints.push({
        x: data.perfusionActualTimepoint!,
        y: data.value,
        //@ts-expect-error: we are using a custom property
        volume: data.volume
      });
    } else if (data.medicationType === 'infusion') {
      glucoseMarkerValues?.push({
        x: data.perfusionActualTimepoint!,
        y: glucoseInfusionY,
        //@ts-expect-error: we are using a custom property
        volume: data.volume
      });
    }
  });

  const glucoseMarkerDataset: GraphDataSet = {
    label: 'Dextrose/Glucose/TPN bolus',
    type: 'scatter',
    data: glucoseMarkerPoints,
    backgroundColor: [PUMP_GRAPH_COLORS.GLUCOSE_BOLUS],
    borderColor: [PUMP_GRAPH_COLORS.GLUCOSE_BOLUS],
    pointRadius: 8
  };

  const glucoseInfusionDataset: GraphDataSet = {
    label: 'Dextrose/Glucose infusion',
    data: glucoseMarkerValues,
    borderColor: [PUMP_GRAPH_COLORS.GLUCOSE_INFUSION],
    backgroundColor: [PUMP_GRAPH_COLORS.GLUCOSE_INFUSION],
    pointBorderColor: PUMP_GRAPH_COLORS.GLUCOSE_INFUSION,
    pointRadius: 4
  };

  const datasets = [dataset];

  if (!showLiverKgData.value) {
    datasets.push(pumpLiverDataset, minDataset, maxDataset);
  }

  if (!showLiverKgData.value && pumpMinValues2.length > 0 && pumpMaxValues2.length > 0) {
    datasets.push(minDataset2);
    datasets.push(maxDataset2);
  }

  if (localField.value.bicarbonateData && localField.value.bicarbonateData.length > 0) {
    datasets.push(markerDataset);
  }

  if (localField.value.glucoseData && localField.value.glucoseData.length > 0) {
    if (glucoseMarkerDataset.data.length > 0) {
      datasets.push(glucoseMarkerDataset);
    } 
    
    if (glucoseMarkerValues.length > 0 && values.length > 0) {
      glucoseInfusionDataset.data = [{
        x: values[0]?.x ?? 0,
        y: glucoseInfusionY
      }, ...glucoseMarkerValues];
      datasets.push(glucoseInfusionDataset);
    }
  }

  const tpnMarkerPoints: GraphDatasetData[] = [];
  const tpnMarkerValues: GraphDatasetData[] = [];

  localField.value.tpnData?.forEach(data => {
    if (data.medicationType === 'bolus') {
      tpnMarkerPoints.push({
        x: data.perfusionActualTimepoint!,
        y: data.value,
        //@ts-expect-error: we are using a custom property
        volume: data.volume
      });
    } else if (data.medicationType === 'infusion') {
      tpnMarkerValues?.push({
        x: data.perfusionActualTimepoint!,
        y: tpnInfusionY,
        //@ts-expect-error: we are using a custom property
        volume: data.volume
      });
    }
  });

  const tpnMarkerDataset: GraphDataSet = {
    label: 'TPN bolus',
    type: 'scatter',
    data: tpnMarkerPoints,
    backgroundColor: [PUMP_GRAPH_COLORS.TPN_BOLUS],
    borderColor: [PUMP_GRAPH_COLORS.TPN_BOLUS],
    pointRadius: 8
  };

  const tpnInfusionDataset: GraphDataSet = {
    label: 'TPN infusion',
    data: tpnMarkerValues,
    borderColor: [PUMP_GRAPH_COLORS.TPN_INFUSION],
    backgroundColor: [PUMP_GRAPH_COLORS.TPN_INFUSION],
    pointBorderColor: PUMP_GRAPH_COLORS.TPN_INFUSION,
    pointRadius: 4
  };

  if (localField.value.tpnData && localField.value.tpnData.length > 0) {
    if (tpnMarkerDataset.data.length > 0) {
      datasets.push(tpnMarkerDataset);
    } 
    
    if (tpnMarkerValues.length > 0 && values.length > 0) {
      tpnInfusionDataset.data = [...tpnMarkerValues, {
        x: values[0]?.x ?? 0,
        y: tpnInfusionY
      }];
      datasets.push(tpnInfusionDataset);
    }
  }

  const centerValues: GraphDatasetData[] = [];
  const centerMinValues: GraphDatasetData[] = [];
  const centerMaxValues: GraphDatasetData[] = [];
  const centerMinValues2: GraphDatasetData[] = [];
  const centerMaxValues2: GraphDatasetData[] = [];

  for (const data of props.field.centerHistoricData || []) {
    if (!showBaseLineData.value && data.perfusionActualTimepoint < 0) {
      continue;
    }

    centerValues.push({
      x: data.perfusionActualTimepoint!,
      y: data.value
    });
    centerMinValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymin
    });
    centerMaxValues.push({
      x: data.perfusionActualTimepoint,
      y: data.ymax
    });
    if (data.ymin2 !== null && data.ymin2 !== undefined && data.ymax2 !== null && data.ymax2 !== undefined) {
      centerMinValues2.push({
        x: data.perfusionActualTimepoint,
        y: data.ymin2
      });
      centerMaxValues2.push({
        x: data.perfusionActualTimepoint,
        y: data.ymax2
      });
    }
  }

  const centerLiverDataset: GraphDataSet = {
    label: `Mean trajectory (${props.field.centerLiverCount} Center livers)`,
    tooltipLabel: 'Mean',
    data: centerValues,
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
    borderColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderColor: '#bf02ef',
    pointHoverBorderColor: '#bf02ef'
  };

  const centerMinDataset: GraphDataSet = {
    label: '95% CI of the mean - Center',
    tooltipLabel: '95% CI - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: centerMinValues,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
    fill: '+1'
  };

  const centerMaxDataset: GraphDataSet = {
    label: 'CIupper',
    tooltipLabel: '95% CI - high',
    data: centerMaxValues,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
    fill: false
  };

  const centerMinDataset2: GraphDataSet = {
    label: 'Central 95% of trajectories - Center',
    tooltipLabel: '95% data - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: centerMinValues2,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
    fill: '+1'
  };

  const centerMaxDataset2: GraphDataSet = {
    label: 'CIupper2',
    tooltipLabel: '95% data - high',
    data: centerMaxValues2,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
    fill: false
  };

  if (centerLiverDataset.data.length > 0) {
    datasets.push(centerLiverDataset);
  }

  if (centerMinDataset.data.length > 0 && centerMaxDataset.data.length > 0) {
    datasets.push(centerMinDataset);
    datasets.push(centerMaxDataset);
  }

  if (centerMinValues2.length > 0 && centerMaxValues2.length > 0) {
    datasets.push(centerMinDataset2);
    datasets.push(centerMaxDataset2);
  }

  graphData.value = {
    labels: [],
    datasets
  };
};

const selectedGraphData = ref<FormField>();

const openGraph = () => {
  selectedGraphData.value = localField.value;
};
const closeGraph = () => {
  selectedGraphData.value = undefined;
};
</script>

<template>
  <div class="chart-root" v-if="graphData">
    <div class="label-row-container">
      <span class="spacer"></span>
      <h3 class="chart-title">{{ localField.label }}</h3>
      <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']"
        class="open-graph"
        @click="openGraph"/>
    </div>
    <Chart :type="chartType"
      ref="chartRef"
      :data="graphData"
      :options="graphOptions"
      class="actual-chart"/>

    <div class="group">
      <div class="baseline-label" v-if="chartType === 'line'">Show baseline data</div>
      <Checkbox v-model="showBaseLineData"
        binary
        v-if="chartType === 'line'"
        inputId="baseline"
        name="baseline"
        value="baseline"/>

      <div class="liver-kg-label" v-if="field.showPerLiverKg">Per liver kg</div>
      <Checkbox v-model="showLiverKgData"
        binary
        v-if="field.showPerLiverKg"
        inputId="liver-kg"
        name="liver-kg"
        value="liver-kg"/>
    </div>
    <div class="loader" v-if="field.loading">
      <ProgressSpinner strokeWidth="4" />
    </div>

    <CustomButton :text="'Reset'"
      @onClick="reset"
      v-if="chartType === 'line'"
      class="reset-zoom-button"
      :type="ButtonType.OUTLINED"/>
  </div>

  <DataGraphDialog :field="selectedGraphData"
    :timeSeriesTimepoints="timeSeriesTimepoints"
    :visible="selectedGraphData != undefined"
    :dialogLabel="selectedGraphData.variableName!"
    @onClose="closeGraph"
    v-if="selectedGraphData"/>
</template>

<style lang="scss" scoped>
@import "../../../assets/variables.module";
.chart-root {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 500px;
}
.p-chart {
  width: 100%;
  height: 100%;
}
.actual-chart {
  position: relative;
  height: 100%;
  width: 100%;
  flex-grow: 1;
}
.chart-title {
  font-weight: 600;
  padding-top: 16px;
  padding-bottom: 16px;
}
.label-row-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.open-graph {
  font-size: 18px;
  margin-right: 16px;
  cursor: pointer;
}
.filters-container {
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px;
}

.filter-button {
  padding: 8px 12px;
  background-color: #eee;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
}

.selected-filter-button {
  background-color: $primary-color;
  color: white;
}

@media (max-width: 768px) {
  .chart-root {
    padding: 8px;
  }
}
.loader {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.group {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .baseline-label, .liver-kg-label {
    margin-right: 10px;
    font-weight: bold;
  }

  .liver-kg-label {
    margin-left: 32px;
  }
}
.reset-zoom-button {
  margin-top: 16px;
  align-self: center;
}
</style>
